import { useState, type FormEvent } from 'react';
import {
  useAccounts,
  useCreateAccount,
  useDeleteAccount,
  useUpdateAccount,
  accountCategoryLabels,
} from '../hooks/useAccounts';
import type { Account, AccountCategory } from '../types';

const categories: AccountCategory[] = ['asset', 'liability', 'equity', 'revenue', 'expense'];

export default function AccountsPage() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data: accounts, isLoading, error } = useAccounts(includeInactive);
  const createMut = useCreateAccount();
  const updateMut = useUpdateAccount();
  const deleteMut = useDeleteAccount();

  const [editing, setEditing] = useState<Account | null>(null);
  const [showForm, setShowForm] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const input = {
      code: String(fd.get('code')),
      name: String(fd.get('name')),
      category: fd.get('category') as AccountCategory,
      ntaCategoryCode: (String(fd.get('ntaCategoryCode')) || null) as string | null,
    };

    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, ...input });
      } else {
        await createMut.mutateAsync(input);
      }
      setEditing(null);
      setShowForm(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : '保存失敗');
    }
  };

  const onDelete = async (acc: Account) => {
    if (!confirm(`「${acc.name}」を非表示にしますか？（過去仕訳は保持されます）`)) return;
    await deleteMut.mutateAsync(acc.id);
  };

  return (
    <div>
      <div className="page-header">
        <h2>勘定科目マスタ</h2>
        <div className="toolbar">
          <label>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />{' '}
            非表示も含む
          </label>
          <button
            className="primary"
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
          >
            + 新規追加
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{(error as Error).message}</div>}

      {showForm && (
        <div className="card">
          <h3>{editing ? '編集' : '新規追加'}</h3>
          <form onSubmit={onSubmit}>
            <div className="form-row">
              <label>科目コード *</label>
              <input name="code" defaultValue={editing?.code ?? ''} required maxLength={20} />
            </div>
            <div className="form-row">
              <label>科目名 *</label>
              <input name="name" defaultValue={editing?.name ?? ''} required maxLength={100} />
            </div>
            <div className="form-row">
              <label>分類 *</label>
              <select name="category" defaultValue={editing?.category ?? 'expense'} required>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {accountCategoryLabels[c]}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <label>国税庁区分コード</label>
              <input
                name="ntaCategoryCode"
                defaultValue={editing?.ntaCategoryCode ?? ''}
                maxLength={20}
                placeholder="例: A01, B04, C10"
              />
            </div>
            <div className="toolbar">
              <button type="submit" className="primary" disabled={createMut.isPending || updateMut.isPending}>
                保存
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditing(null);
                }}
              >
                キャンセル
              </button>
            </div>
          </form>
        </div>
      )}

      {isLoading && <div>読み込み中...</div>}

      {accounts && (
        <table>
          <thead>
            <tr>
              <th style={{ width: 100 }}>コード</th>
              <th>科目名</th>
              <th style={{ width: 100 }}>分類</th>
              <th style={{ width: 140 }}>国税庁区分コード</th>
              <th style={{ width: 100 }}>状態</th>
              <th style={{ width: 160 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id}>
                <td>{a.code}</td>
                <td>{a.name}</td>
                <td>{accountCategoryLabels[a.category]}</td>
                <td>{a.ntaCategoryCode ?? <span className="text-muted">未設定</span>}</td>
                <td>
                  {a.isActive ? (
                    <span className="badge active">有効</span>
                  ) : (
                    <span className="badge voided">非表示</span>
                  )}
                </td>
                <td>
                  <button
                    onClick={() => {
                      setEditing(a);
                      setShowForm(true);
                    }}
                  >
                    編集
                  </button>{' '}
                  {a.isActive === 1 && (
                    <button onClick={() => onDelete(a)} className="danger">
                      非表示
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
